import { languageCodes, languageCodesSchema } from '@inkitt/translations-galatea/config/galateaI18nConfig';
import { createInstance, type Resource } from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import { initReactI18next } from 'react-i18next/initReactI18next';
import { brandInterpolation, DEFAULT_LANGUAGE, i18nConfig } from './i18nConfig';
import { sanitizeLanguageCode } from './sanitizer';
import type { WebAppName } from './types/WebAppName';

intervalPlural.setOptions({
  intervalSeparator: ';',
  // * NOTE: We use the regex from before version 3.0 to avoid breaking current interval translations defined in LocalLazy.
  // * For further context, the new default is `/\((\S*)\).*?\[((.|\n)*)\]/` but using this would break interval translations.
  intervalRegex: /\((\S*)\).*{((.|\n)*)}/,
  intervalSuffix: '_interval',
});

/**
 * Client-side i18n initialization that requires resources to be provided.
 * This version does NOT include resourcesToBackend to avoid webpack bundling
 * all locale files in the client bundle. Resources should be fetched server-side
 * and passed to this function.
 */
export const i18nInit = async ({
  appName,
  locale: rawLocale = DEFAULT_LANGUAGE,
  namespaces,
  resources,
  i18nInstance,
}: {
  appName: WebAppName;
  locale: string;
  namespaces: string[];
  resources: Resource; // Required on client-side
  i18nInstance?: ReturnType<typeof createInstance>;
}) => {
  i18nInstance = i18nInstance || createInstance();

  i18nInstance.use(initReactI18next);
  i18nInstance.use(intervalPlural);

  if (!namespaces.includes('common')) {
    // Default namespace
    namespaces.push('common');
  }

  const languageCode = sanitizeLanguageCode(rawLocale);
  const locale = languageCodesSchema.parse(languageCode);

  await i18nInstance.init({
    ...i18nConfig,
    interpolation: brandInterpolation(appName),
    lng: locale,
    resources,
    // Note: All language codes are included and the website's middleware will handle which locales are supported by the web app.
    supportedLngs: languageCodes,
    defaultNS: namespaces[0],
    fallbackNS: namespaces[0],
    ns: namespaces,
    preload: [], // No preloading needed when resources are provided
  });

  return {
    t: i18nInstance.t,
    resources: i18nInstance.services.resourceStore.data,
    namespaces,
    appName,
  };
};
